import { useQuery } from '@apollo/client'
import { GetCandidatesForClientPortalSqlQuery } from '__generated__/graphql'
import { GetCandidatesForClientPortalSql } from 'client/candidate-table-temp/graphql/candidateSearch.gql'
import { GetProcessStatusOptions } from 'client/candidate-table-temp/graphql/processStatusOptions.gql'
import { useCandidateSqlSearch } from 'client/candidate-table-temp/hooks/useCandidateSqlSearch'
import useToast from 'shared/components/Toast/useToast'
import {
    type CandidateFilterState,
    type PaginationState,
    mapSortIndexFieldToString,
    hasFilters,
    toGraphQLFilters,
    getViewFromTab,
    processStatusGroupToString,
} from './rosterSearchWithFiltersUtils'

export type RosterCandidateTableRowModel =
    GetCandidatesForClientPortalSqlQuery['candidateSearchSql']['data'][number]

export type FilterState = CandidateFilterState

export { type CandidateFilterState, type PaginationState }

interface UseRosterSearchWithFiltersConfig {
    rosterId?: string
}

export function useRosterSearchWithFilters({
    rosterId,
}: UseRosterSearchWithFiltersConfig) {
    const toast = useToast()

    const { data: processStatusData, loading: statusLoading } = useQuery(
        GetProcessStatusOptions
    )
    const processStatusOptions = processStatusData?.processStatuses || []

    const {
        activeSortFields,
        page,
        limit,
        setPage,
        setLimit,
        filters,
        setFilters,
        clearFilters,
    } = useCandidateSqlSearch()

    const sort = activeSortFields.map((s) => ({
        field: mapSortIndexFieldToString(s.field),
        order: s.order,
    }))

    const currentView = getViewFromTab(filters.processStatusGroup)
    const processStatusGroup = processStatusGroupToString(
        filters.processStatusGroup
    )

    const { data: queryData, loading } = useQuery(
        GetCandidatesForClientPortalSql,
        {
            variables: {
                rosterId: rosterId || undefined,
                referenceRosterIds: [],
                filters: toGraphQLFilters(
                    filters,
                    processStatusOptions,
                    processStatusGroup
                ),
                page,
                limit,
                sort,
            },
            skip: statusLoading,
            onError: (error) => {
                toast({
                    title: 'Error',
                    message: 'Unable to fetch candidates, please try again',
                    severity: 'error',
                })
                console.error('Error fetching candidates:', error)
            },
        }
    )

    const data = queryData?.candidateSearchSql?.data || []
    const pageInfo = queryData?.candidateSearchSql?.pageInfo
    const totalItems: number | undefined = pageInfo?.itemTotal

    return {
        page,
        limit,
        data,
        loading,
        totalItems: typeof totalItems === 'number' ? totalItems : undefined,
        filters,
        hasActiveFilters: hasFilters(filters),
        currentView,
        setPage,
        setLimit,
        setFilters,
        clearFilters,
    }
}
